import React, { Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import AppLayout from "@/components/AppLayout";
import { PageLoader } from "@/components/LoadingSpinner";

// ─── Eager-loaded pages (critical first paint) ─────────────────
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import NotFound from "./pages/NotFound";

// ─── Lazy-loaded chunks ────────────────────────────────────────

// Auth & Onboarding
const PasswordReset = React.lazy(() => import("./pages/PasswordReset"));
const EmailVerification = React.lazy(() => import("./pages/EmailVerification"));
const Onboarding = React.lazy(() => import("./pages/Onboarding"));

// Home & Dashboard
const Index = React.lazy(() => import("./pages/Index"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));

// Profile & Portfolio
const Profile = React.lazy(() => import("./pages/Profile"));
const MusicianProfile = React.lazy(() => import("./pages/MusicianProfile"));
const Portfolio = React.lazy(() => import("./pages/Portfolio"));
const PortfolioTracks = React.lazy(() => import("./pages/PortfolioTracks"));
const PortfolioPlaylists = React.lazy(() => import("./pages/PortfolioPlaylists"));
const TrackDetail = React.lazy(() => import("./pages/TrackDetail"));
const PublicPortfolio = React.lazy(() => import("./pages/PublicPortfolio"));

// Matching & Search
const Matching = React.lazy(() => import("./pages/Matching"));
const Search = React.lazy(() => import("./pages/Search"));

// Messages & Collaborations
const Messages = React.lazy(() => import("@/pages/Messages"));
const Collaborations = React.lazy(() => import("./pages/Collaborations"));

// Projects
const Projects = React.lazy(() => import("./pages/Projects"));
const ProjectDetail = React.lazy(() => import("./pages/ProjectDetail"));
const ProjectNew = React.lazy(() => import("./pages/ProjectNew"));
const ProjectManage = React.lazy(() => import("./pages/ProjectManage"));
const ProjectTimelinePage = React.lazy(() => import("./pages/ProjectTimeline"));
const ProjectTasksPage = React.lazy(() => import("./pages/ProjectTasks"));
const ProjectBudgetPage = React.lazy(() => import("./pages/ProjectBudget"));
const ProjectResourcesPage = React.lazy(() => import("./pages/ProjectResources"));
const MyProjects = React.lazy(() => import("./pages/MyProjects"));
const MyApplications = React.lazy(() => import("./pages/MyApplications"));

// Marketplace
const Marketplace = React.lazy(() => import("./pages/Marketplace"));
const MarketplaceGigDetail = React.lazy(() => import("./pages/MarketplaceGigDetail"));
const MarketplaceMyGigs = React.lazy(() => import("./pages/MarketplaceMyGigs"));
const MarketplaceVenues = React.lazy(() => import("./pages/MarketplaceVenues"));
const MarketplaceVenueDetail = React.lazy(() => import("./pages/MarketplaceVenueDetail"));
const MarketplaceMyVenues = React.lazy(() => import("./pages/MarketplaceMyVenues"));
const MarketplaceMyBookings = React.lazy(() => import("./pages/MarketplaceMyBookings"));
const CreateVenue = React.lazy(() => import("./pages/CreateVenue"));
const CreateGig = React.lazy(() => import("./pages/CreateGig"));

// Administration (ECO-035)
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = React.lazy(() => import("./pages/admin/AdminUsers"));
const AdminContent = React.lazy(() => import("./pages/admin/AdminContent"));
const AdminMetrics = React.lazy(() => import("./pages/admin/AdminMetrics"));

// Settings & Notifications
const Settings = React.lazy(() => import("./pages/Settings"));
const NotificationsPage = React.lazy(() => import("./pages/NotificationsPage"));

// Legal
const TermsOfService = React.lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));

const queryClient = new QueryClient();

const App = () => (
  <ThemeProvider defaultTheme="light">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <LanguageProvider>
              <Suspense fallback={<PageLoader text="Cargando..." />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<Landing />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/password-reset" element={<PasswordReset />} />
                  <Route path="/verify-email" element={<EmailVerification />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />

                  {/* Onboarding — standalone, no AppLayout */}
                  <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

                  {/* Authenticated home feed */}
                  <Route path="/home" element={<ProtectedRoute><AppLayout><Index /></AppLayout></ProtectedRoute>} />
                  <Route path="/dashboard" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><AppLayout><Settings /></AppLayout></ProtectedRoute>} />
                  <Route path="/notifications" element={<ProtectedRoute><AppLayout><NotificationsPage /></AppLayout></ProtectedRoute>} />
                  <Route path="/collaborations" element={<ProtectedRoute><AppLayout><Collaborations /></AppLayout></ProtectedRoute>} />

                  {/* Profile & Portfolio Routes */}
                  <Route path="/profile" element={<ProtectedRoute><AppLayout><Profile /></AppLayout></ProtectedRoute>} />
                  <Route path="/portfolio" element={<ProtectedRoute><AppLayout><Portfolio /></AppLayout></ProtectedRoute>} />
                  <Route path="/portfolio/tracks" element={<ProtectedRoute><AppLayout><PortfolioTracks /></AppLayout></ProtectedRoute>} />
                  <Route path="/portfolio/playlists" element={<ProtectedRoute><AppLayout><PortfolioPlaylists /></AppLayout></ProtectedRoute>} />
                  <Route path="/track/:id" element={<ProtectedRoute><AppLayout><TrackDetail /></AppLayout></ProtectedRoute>} />
                  <Route path="/musician/:id" element={<ProtectedRoute><AppLayout><MusicianProfile /></AppLayout></ProtectedRoute>} />
                  <Route path="/musician/:id/portfolio" element={<ProtectedRoute><AppLayout><PublicPortfolio /></AppLayout></ProtectedRoute>} />

                  {/* Messages Routes */}
                  <Route path="/messages" element={<ProtectedRoute><AppLayout><Messages /></AppLayout></ProtectedRoute>} />
                  <Route path="/messages/:conversationId" element={<ProtectedRoute><AppLayout><Messages /></AppLayout></ProtectedRoute>} />

                  {/* Matching & Search Routes */}
                  <Route path="/matching" element={<ProtectedRoute><AppLayout><Matching /></AppLayout></ProtectedRoute>} />
                  <Route path="/search" element={<ProtectedRoute><AppLayout><Search /></AppLayout></ProtectedRoute>} />

                  {/* Projects Routes */}
                  <Route path="/projects" element={<ProtectedRoute><AppLayout><Projects /></AppLayout></ProtectedRoute>} />
                  <Route path="/projects/new" element={<ProtectedRoute><AppLayout><ProjectNew /></AppLayout></ProtectedRoute>} />
                  <Route path="/projects/my-projects" element={<ProtectedRoute><AppLayout><MyProjects /></AppLayout></ProtectedRoute>} />
                  <Route path="/projects/my-applications" element={<ProtectedRoute><AppLayout><MyApplications /></AppLayout></ProtectedRoute>} />
                  <Route path="/projects/:id" element={<ProtectedRoute><AppLayout><ProjectDetail /></AppLayout></ProtectedRoute>} />
                  <Route path="/projects/:id/manage" element={<ProtectedRoute><AppLayout><ProjectManage /></AppLayout></ProtectedRoute>} />
                  <Route path="/projects/:id/timeline" element={<ProtectedRoute><AppLayout><ProjectTimelinePage /></AppLayout></ProtectedRoute>} />
                  <Route path="/projects/:id/tasks" element={<ProtectedRoute><AppLayout><ProjectTasksPage /></AppLayout></ProtectedRoute>} />
                  <Route path="/projects/:id/budget" element={<ProtectedRoute><AppLayout><ProjectBudgetPage /></AppLayout></ProtectedRoute>} />
                  <Route path="/projects/:id/resources" element={<ProtectedRoute><AppLayout><ProjectResourcesPage /></AppLayout></ProtectedRoute>} />

                  {/* Marketplace Routes */}
                  <Route path="/marketplace" element={<ProtectedRoute><AppLayout><Marketplace /></AppLayout></ProtectedRoute>} />
                  <Route path="/marketplace/gigs/:id" element={<ProtectedRoute><AppLayout><MarketplaceGigDetail /></AppLayout></ProtectedRoute>} />
                  <Route path="/marketplace/my-gigs" element={<ProtectedRoute><AppLayout><MarketplaceMyGigs /></AppLayout></ProtectedRoute>} />
                  <Route path="/marketplace/my-bookings" element={<ProtectedRoute><AppLayout><MarketplaceMyBookings /></AppLayout></ProtectedRoute>} />
                  <Route path="/marketplace/create-gig" element={<ProtectedRoute><AppLayout><CreateGig /></AppLayout></ProtectedRoute>} />
                  <Route path="/marketplace/venues" element={<ProtectedRoute><AppLayout><MarketplaceVenues /></AppLayout></ProtectedRoute>} />
                  <Route path="/marketplace/venues/:id" element={<ProtectedRoute><AppLayout><MarketplaceVenueDetail /></AppLayout></ProtectedRoute>} />
                  <Route path="/marketplace/my-venues" element={<ProtectedRoute><AppLayout><MarketplaceMyVenues /></AppLayout></ProtectedRoute>} />
                  <Route path="/marketplace/create-venue" element={<ProtectedRoute><AppLayout><CreateVenue /></AppLayout></ProtectedRoute>} />

                  {/* Administrative Routes */}
                  <Route path="/admin" element={<ProtectedRoute><AdminRoute><AppLayout><AdminDashboard /></AppLayout></AdminRoute></ProtectedRoute>} />
                  <Route path="/admin/users" element={<ProtectedRoute><AdminRoute><AppLayout><AdminUsers /></AppLayout></AdminRoute></ProtectedRoute>} />
                  <Route path="/admin/content" element={<ProtectedRoute><AdminRoute><AppLayout><AdminContent /></AppLayout></AdminRoute></ProtectedRoute>} />
                  <Route path="/admin/metrics" element={<ProtectedRoute><AdminRoute><AppLayout><AdminMetrics /></AppLayout></AdminRoute></ProtectedRoute>} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
